import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Input, Row, Col, Card, Alert, Spin } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import { getUserByEmail } from '../../api/users';
import type { Language, Currency, User } from '../../types/users';

interface UserFormSectionProps {
  form: any;
  initialValues?: {
    user?: {
      id?: string;
      displayName?: string;
      email?: string;
      phoneNumber?: string;
      username?: string;
      language?: Language;
      currency?: Currency;
    };
  };
  onUserFound?: (user: User | null) => void;
  fieldIds?: {
    email?: string;
    displayName?: string;
    phoneNumber?: string;
  };
}

const UserFormSection: React.FC<UserFormSectionProps> = ({ form, initialValues, onUserFound, fieldIds }) => {
  const { t } = useTranslation(['common', 'users']);
  const { firestoreUser } = useAuth();
  const isEditMode = !!initialValues?.user?.id;
  
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailStatus, setEmailStatus] = useState<{
    type: 'success' | 'warning' | 'error' | 'info';
    message: string;
    existingUser?: User;
    /** True only when we reuse the same user document (this workspace or no workspace). */
    disableUserFields?: boolean;
  } | null>(null);

  // Generate username slug from displayName
  const generateUsername = (displayName: string): string => {
    return displayName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric chars with hyphens
      .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
      .substring(0, 30); // Limit length
  };

  // Check for existing user by email
  const checkExistingUser = async (email: string) => {
    if (!email || !firestoreUser?.workspace?.id) return;
    
    setEmailChecking(true);
    setEmailStatus(null);
    
    try {
      const existingUsers = await getUserByEmail(email);
      
      if (existingUsers.length === 0) {
        // No user found - will create new user
        setEmailStatus({
          type: 'info',
          message: 'No existing user found. A new user account will be created.'
        });
        // Clear any existing user data
        form.setFieldsValue({
          user: {
            ...form.getFieldValue('user'),
            id: undefined,
            displayName: '',
            phoneNumber: '',
            username: ''
          }
        });
        if (onUserFound) onUserFound(null);
      } else {
        // Found existing users
        const userInSameWorkspace = existingUsers.find(u => u.workspace?.id === firestoreUser.workspace?.id);
        const userWithoutWorkspace = existingUsers.find(u => !u.workspace);
        
        if (userInSameWorkspace) {
          // User already exists in this workspace
          setEmailStatus({
            type: 'warning',
            message: `User already exists in this workspace as "${userInSameWorkspace.displayName}".`,
            existingUser: userInSameWorkspace,
            disableUserFields: true,
          });
          // Pre-fill form with existing user data
          form.setFieldsValue({
            user: {
              ...form.getFieldValue('user'),
              id: userInSameWorkspace.id,
              displayName: userInSameWorkspace.displayName,
              phoneNumber: userInSameWorkspace.phoneNumber || '',
              username: userInSameWorkspace.username
            }
          });
          if (onUserFound) onUserFound(userInSameWorkspace);
        } else if (userWithoutWorkspace) {
          // User exists but has no workspace - can be used for resident
          setEmailStatus({
            type: 'success',
            message: `Found existing user "${userWithoutWorkspace.displayName}" without workspace. Will use this account.`,
            existingUser: userWithoutWorkspace,
            disableUserFields: true,
          });
          // Pre-fill form with existing user data
          form.setFieldsValue({
            user: {
              ...form.getFieldValue('user'),
              id: userWithoutWorkspace.id,
              displayName: userWithoutWorkspace.displayName,
              phoneNumber: userWithoutWorkspace.phoneNumber || '',
              username: userWithoutWorkspace.username
            }
          });
          if (onUserFound) onUserFound(userWithoutWorkspace);
        } else {
          // User exists in different workspace — new user doc for this workspace; pre-fill from profile, fields stay editable
          const otherWorkspaceUser = existingUsers[0];
          const displayName = otherWorkspaceUser.displayName || '';
          setEmailStatus({
            type: 'warning',
            message: `User exists in workspace "${otherWorkspaceUser.workspace?.name}". A new user account will be created for this workspace.`,
            existingUser: otherWorkspaceUser,
            disableUserFields: false,
          });
          form.setFieldsValue({
            user: {
              ...form.getFieldValue('user'),
              id: undefined,
              displayName,
              phoneNumber: otherWorkspaceUser.phoneNumber || '',
              username: displayName ? generateUsername(displayName) : '',
            },
          });
          if (onUserFound) onUserFound(null);
        }
      }
    } catch (error) {
      console.error('Error checking existing user:', error);
      setEmailStatus({
        type: 'error',
        message: 'Error checking for existing user. Please try again.'
      });
    } finally {
      setEmailChecking(false);
    }
  };

  // Watch displayName changes to update username
  useEffect(() => {
    const displayName = form.getFieldValue(['user', 'displayName']);
    if (displayName) {
      form.setFieldsValue({
        user: {
          ...form.getFieldValue('user'),
          username: generateUsername(displayName)
        }
      });
    }
  }, [form.getFieldValue(['user', 'displayName'])]);

  const disableUserFields = emailStatus?.disableUserFields === true;

  return (
    <Card>
      {/* Email field first */}
      {!isEditMode && (
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name={['user', 'email']}
              label={t('users:fields.email')}
              rules={[
                { required: true, message: t('common:validation.required') },
                { type: 'email', message: t('common:validation.email') }
              ]}
            >
              <Input 
                id={fieldIds?.email}
                onBlur={(e) => checkExistingUser(e.target.value)}
                suffix={emailChecking ? <Spin size="small" /> : null}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      {/* Email status message */}
      {emailStatus && (
        <Row gutter={16}>
          <Col span={24}>
            <Alert
              message={emailStatus.message}
              type={emailStatus.type}
              showIcon
              style={{ marginBottom: 16 }}
            />
          </Col>
        </Row>
      )}

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name={['user', 'displayName']}
            label={t('users:fields.displayName')}
            rules={[{ required: true, message: t('common:validation.required') }]}
            extra={disableUserFields ? 'Using existing user data' : undefined}
          >
            <Input id={fieldIds?.displayName} disabled={disableUserFields} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={['user', 'phoneNumber']}
            label={t('users:fields.phone')}
            rules={[{ required: true, message: t('common:validation.required') }]}
            initialValue={initialValues?.user?.phoneNumber}
            extra={disableUserFields ? 'Using existing user data' : undefined}
          >
            <Input id={fieldIds?.phoneNumber} disabled={disableUserFields} />
          </Form.Item>
        </Col>
      </Row>

      {!isEditMode && (
        <>
          {/* Hidden fields for workspace and role - only in create mode */}
          <Form.Item name={['workspace', 'id']} hidden initialValue={firestoreUser?.workspace?.id}>
            <Input />
          </Form.Item>
          <Form.Item name={['workspace', 'name']} hidden initialValue={firestoreUser?.workspace?.name}>
            <Input />
          </Form.Item>
          <Form.Item name={['workspace', 'slug']} hidden initialValue={firestoreUser?.workspace?.slug}>
            <Input />
          </Form.Item>
          <Form.Item name={['userRole']} hidden initialValue="resident">
            <Input />
          </Form.Item>
          {/* Hidden fields for language and currency - only in create mode */}
          <Form.Item name={['user', 'language']} hidden initialValue={firestoreUser?.language}>
            <Input />
          </Form.Item>
          <Form.Item name={['user', 'currency']} hidden initialValue={firestoreUser?.currency}>
            <Input />
          </Form.Item>
        </>
      )}
      {/* Hidden field for username - always needed */}
      <Form.Item name={['user', 'username']} hidden>
        <Input />
      </Form.Item>
    </Card>
  );
};

export default UserFormSection; 